<script setup lang="ts">
import { faImageSlash, faSpinnerThird } from '@fortawesome/pro-light-svg-icons';
import { useImage } from '@vueuse/core';
import { computed, watch } from 'vue';
import FontAwesomeIcon from './FontAwesomeIcon.vue';

export interface SrcSet {
  src: string;
  mime?: string;
  media?: string;
}

const props = withDefaults(
  defineProps<{
    src: string | SrcSet[];
    alt: string;
    fit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    position?: 'bottom' | 'center' | 'left' | 'right' | 'top';
    loading?: 'eager' | 'lazy';
    imageClass?: string;
  }>(),
  {
    srcset: () => [],
    fit: 'cover',
    position: 'center',
    loading: undefined,
    imageClass: undefined,
  },
);

const emit = defineEmits<{
  (e: 'load'): void;
}>();

const alternateSrcs = computed(() => {
  if (typeof props.src !== 'string') {
    return props.src.slice(0, -1);
  }

  return [];
});

const imageSrc = computed(() => {
  if (typeof props.src !== 'string') {
    return props.src[props.src.length - 1].src;
  }

  return props.src;
});

const { isLoading, error } = useImage(() => ({ src: imageSrc.value }));

watch(isLoading, (newVal, oldVal) => {
  if (!newVal && oldVal) {
    emit('load');
  }
});
</script>

<template>
  <picture class="relative">
    <source
      v-for="srcset in alternateSrcs"
      :key="srcset.src"
      :srcset="srcset.src"
      :type="srcset.mime"
      :media="srcset.media"
    />
    <Transition name="fade" mode="out-in">
      <div v-if="error" class="flex w-full h-full justify-center items-center opacity-25">
        <FontAwesomeIcon :icon="faImageSlash" />
      </div>
      <div v-else-if="isLoading" class="flex w-full h-full justify-center items-center opacity-50">
        <FontAwesomeIcon :icon="faSpinnerThird" spin />
      </div>
      <img
        v-else
        :src="imageSrc"
        :alt="alt"
        :loading="loading"
        :style="{ objectFit: fit, objectPosition: position }"
        class="w-full h-full"
        :class="imageClass"
      />
    </Transition>
  </picture>
</template>
